import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Col,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { ErrorMessage, Field, Form, Formik } from "formik";
import {
  BAG_COUNT_OPTIONS,
  COVERAGE_OPTIONS,
  formatCurrency,
  getCoverageDates,
  getPolicyModalInitialValues,
  issueBagProtectionPolicySchema,
  syncCoverages,
  validateCoverages,
} from "partnerportal/helpers/utils";

const GENERIC_ERROR_MESSAGE = (
  <>
    Unable to generate the tags. Please contact{" "}
    <a href="mailto:support@tag8.co.in">support@tag8.co.in</a>
  </>
);

const INSUFFICIENT_WALLET_MESSAGE =
  "Insufficient wallet balance. Unable to issue policy.";

const getDebitAmount = (bagCoverages, rateOfInsurance) => {
  const coverages = (bagCoverages || [])
    .map(Number)
    .filter((c) => !Number.isNaN(c) && c > 0);
  if (!coverages.length || !rateOfInsurance) return null;

  let total = 0;
  for (const coverage of coverages) {
    const rate = Number(rateOfInsurance[String(coverage)]);
    if (Number.isNaN(rate) || rate <= 0) return null;
    total += rate;
  }
  return total;
};

const IssuePolicyModal = ({
  isOpen,
  toggle,
  onSuccess,
  axiosInstance,
  onError,
  policyToEdit,
}) => {
  const [submitError, setSubmitError] = useState(null);
  const [walletBalance, setWalletBalance] = useState(null);
  const [rateOfInsurance, setRateOfInsurance] = useState({});
  const [walletLoading, setWalletLoading] = useState(false);
  const isEditMode = Boolean(policyToEdit);

  const fetchWallet = useCallback(async () => {
    if (!axiosInstance || isEditMode) return;
    setWalletLoading(true);
    try {
      const { data } = await axiosInstance.get("/api/partner/companywallet");
      if (data?.status && data?.data) {
        setWalletBalance(Number(data.data.wallet_balance) || 0);
        setRateOfInsurance(data.data.rate_of_insurance || {});
      }
    } catch (error) {
      console.log(error);
    } finally {
      setWalletLoading(false);
    }
  }, [axiosInstance, isEditMode]);

  useEffect(() => {
    if (isOpen && !isEditMode) {
      fetchWallet();
    }
  }, [isOpen, isEditMode, fetchWallet]);

  // Reset the inline error whenever the modal is opened/closed
  const handleToggle = () => {
    setSubmitError(null);
    toggle();
  };

  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    setSubmitError(null);
    try {
      const coverages = values.bag_coverages
        .map(Number)
        .map((c) => (Number.isNaN(c) ? 0 : c));

      if (!isEditMode) {
        const debitAmount = getDebitAmount(coverages, rateOfInsurance);
        if (
          debitAmount != null &&
          walletBalance != null &&
          walletBalance < debitAmount
        ) {
          setSubmitError(INSUFFICIENT_WALLET_MESSAGE);
          setSubmitting(false);
          return;
        }
      }

      const payload = {
        first_name: values.first_name.trim(),
        last_name: values.last_name.trim(),
        email: values.email.trim(),
        phone_number: values.phone_number.trim(),
        phone_country_code: "+91",
        insurance_value: coverages,
        no_of_bags: Number(values.no_of_bags),
      };

      if (isEditMode) {
        const { data } = await axiosInstance.put(
          `/api/policyupdate/${
            policyToEdit?.policy_number || policyToEdit?._id
          }`,
          payload
        );

        if (data?.status) {
          onSuccess(data?.message || "Policy updated successfully");
          resetForm();
          toggle();
        } else {
          setSubmitError(GENERIC_ERROR_MESSAGE);
          onError?.(data?.message || "Failed , Please contact tag8 support");
        }
        return;
      }

      const coverageDates = getCoverageDates();
      const { data } = await axiosInstance.post("/api/partner/insuranceregister", {
        ...payload,
        ...coverageDates,
      });

      if (data?.status) {
        if (typeof data.wallet_balance === "number") {
          setWalletBalance(data.wallet_balance);
        }
        onSuccess(data?.message || "Policy issued successfully");
        resetForm();
        toggle();
      } else {
        setSubmitError(
          data?.message === INSUFFICIENT_WALLET_MESSAGE
            ? INSUFFICIENT_WALLET_MESSAGE
            : GENERIC_ERROR_MESSAGE
        );
        onError?.(data?.message || "Failed , Please contact tag8 support");
      }
    } catch (error) {
      console.log(error);
      const apiMessage = error?.response?.data?.message;
      setSubmitError(
        apiMessage === INSUFFICIENT_WALLET_MESSAGE
          ? INSUFFICIENT_WALLET_MESSAGE
          : GENERIC_ERROR_MESSAGE
      );
      onError?.(apiMessage || "Failed , Please contact tag8 support");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={handleToggle} size="lg">
      <ModalHeader toggle={handleToggle} tag="h4">
        {isEditMode
          ? "Update Bag Protection Plan"
          : "Issue Bag Protection Plan"}
      </ModalHeader>
      <ModalBody>
        {submitError && (
          <Alert color="danger" toggle={() => setSubmitError(null)}>
            {submitError}
          </Alert>
        )}
        <Formik
          key={
            policyToEdit
              ? policyToEdit?.policy_number || policyToEdit?._id
              : "new-policy"
          }
          initialValues={getPolicyModalInitialValues(policyToEdit)}
          validationSchema={issueBagProtectionPolicySchema}
          validate={validateCoverages}
          onSubmit={handleSubmit}
        >
          {({ values, errors, touched, isSubmitting, setFieldValue }) => {
            const debitAmount = isEditMode
              ? null
              : getDebitAmount(values.bag_coverages, rateOfInsurance);
            const insufficientWallet =
              !isEditMode &&
              debitAmount != null &&
              walletBalance != null &&
              walletBalance < debitAmount;

            return (
              <Form>
                {!isEditMode && (
                  <div className="mb-3 text-muted small">
                    {walletLoading ? (
                      "Checking wallet balance..."
                    ) : (
                      <>
                        Wallet balance:{" "}
                        <strong>
                          {walletBalance == null
                            ? "—"
                            : formatCurrency(walletBalance)}
                        </strong>
                        {debitAmount != null && (
                          <>
                            {" "}
                            · Policy charge:{" "}
                            <strong>{formatCurrency(debitAmount)}</strong>
                            {" "}

                          </>
                        )}
                      </>
                    )}
                  </div>
                )}

                {insufficientWallet && (
                  <Alert color="warning" className="py-2">
                    {INSUFFICIENT_WALLET_MESSAGE}
                  </Alert>
                )}

                <Row>
                  {/* First Name */}
                  <Col md="6">
                    <div className="mb-3">
                      <Label className="form-label">First Name</Label>
                      <Field
                        name="first_name"
                        type="text"
                        maxLength="25"
                        className={
                          "form-control" +
                          (errors.first_name && touched.first_name
                            ? " is-invalid"
                            : "")
                        }
                        disabled={isEditMode}
                      />
                      <ErrorMessage
                        name="first_name"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                  </Col>

                  {/* Last Name */}
                  <Col md="6">
                    <div className="mb-3">
                      <Label className="form-label">Last Name</Label>
                      <Field
                        name="last_name"
                        type="text"
                        maxLength="25"
                        className={
                          "form-control" +
                          (errors.last_name && touched.last_name
                            ? " is-invalid"
                            : "")
                        }
                        disabled={isEditMode}
                      />
                      <ErrorMessage
                        name="last_name"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                  </Col>

                  {/* Email */}
                  <Col md="6">
                    <div className="mb-3">
                      <Label className="form-label">Email</Label>
                      <Field
                        name="email"
                        type="email"
                        maxLength="50"
                        className={
                          "form-control" +
                          (errors.email && touched.email ? " is-invalid" : "")
                        }
                      />
                      <ErrorMessage
                        name="email"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                  </Col>

                  {/* Phone */}
                  <Col md="6">
                    <div className="mb-3">
                      <Label className="form-label">Mobile Number</Label>
                      <div
                        className={
                          "input-group" +
                          (errors.phone_number && touched.phone_number
                            ? " is-invalid"
                            : "")
                        }
                      >
                        <span className="input-group-text">+91</span>
                        <Field name="phone_number">
                          {({ field, form }) => (
                            <input
                              {...field}
                              type="tel"
                              inputMode="numeric"
                              maxLength="10"
                              className={
                                "form-control" +
                                (errors.phone_number && touched.phone_number
                                  ? " is-invalid"
                                  : "")
                              }
                              onChange={(e) => {
                                const digits = e.target.value
                                  .replace(/\D/g, "")
                                  .slice(0, 10);
                                form.setFieldValue(field.name, digits);
                              }}
                              onKeyDown={(e) => {
                                const allowed = [
                                  "Backspace",
                                  "Delete",
                                  "Tab",
                                  "ArrowLeft",
                                  "ArrowRight",
                                  "Home",
                                  "End",
                                ];
                                if (
                                  allowed.includes(e.key) ||
                                  (e.ctrlKey &&
                                    ["a", "c", "v", "x"].includes(
                                      e.key.toLowerCase()
                                    ))
                                )
                                  return;
                                if (!/^\d$/.test(e.key)) e.preventDefault();
                              }}
                            />
                          )}
                        </Field>
                      </div>
                      {errors.phone_number && touched.phone_number && (
                        <div
                          className="text-danger"
                          style={{ fontSize: "0.875em", marginTop: "0.25rem" }}
                        >
                          {errors.phone_number}
                        </div>
                      )}
                    </div>
                  </Col>

                  {/* No of Bags */}
                  <Col md="6">
                    <div className="mb-3">
                      <Label className="form-label">No of Bags</Label>
                      <select
                        value={values.no_of_bags}
                        className={
                          "form-select" +
                          (errors.no_of_bags && touched.no_of_bags
                            ? " is-invalid"
                            : "")
                        }
                        disabled={isEditMode}
                        onChange={(e) => {
                          const count = Number(e.target.value);
                          setFieldValue("no_of_bags", count);
                          // Sync coverage array to new count, preserving existing selections
                          setFieldValue(
                            "bag_coverages",
                            syncCoverages(values.bag_coverages, count)
                          );
                        }}
                      >
                        {BAG_COUNT_OPTIONS.map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                      <ErrorMessage
                        name="no_of_bags"
                        component="div"
                        className="invalid-feedback"
                      />
                    </div>
                  </Col>

                  {/* Coverage per bag — renders exactly no_of_bags fields */}
                  {Array.from({ length: values.no_of_bags }, (_, i) => (
                    <Col md="6" key={i}>
                      <div className="mb-3">
                        <Label className="form-label">
                          Bag {i + 1} - Coverage Amount
                        </Label>
                        <Field
                          as="select"
                          name={`bag_coverages.${i}`}
                          className={
                            "form-select" +
                            (errors.bag_coverages?.[i] &&
                            touched.bag_coverages?.[i]
                              ? " is-invalid"
                              : "")
                          }
                          disabled={isEditMode}
                        >
                          <option value="">Select coverage amount</option>
                          {COVERAGE_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {formatCurrency(opt.value)}
                            </option>
                          ))}
                        </Field>
                        <ErrorMessage
                          name={`bag_coverages.${i}`}
                          component="div"
                          className="invalid-feedback"
                        />
                      </div>
                    </Col>
                  ))}
                </Row>

                <div className="text-end">
                  <Button
                    type="button"
                    color="light"
                    className="me-2"
                    onClick={handleToggle}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    color="primary"
                    disabled={isSubmitting || insufficientWallet}
                  >
                    {isSubmitting ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        {isEditMode ? "Updating..." : "Submitting..."}
                      </>
                    ) : isEditMode ? (
                      "Update"
                    ) : (
                      "Submit"
                    )}
                  </Button>
                </div>
              </Form>
            );
          }}
        </Formik>
      </ModalBody>
    </Modal>
  );
};

export default IssuePolicyModal;
